import { useCallback, useEffect, useState } from 'react';
import {
  DOOR_SPRITES,
  preloadImages,
  warmArmSprites,
  warmButtonSprites,
  warmSingSprites,
} from '../door/preload';

/**
 * The Door. Both the landing page and the app's loading state. (DESIGN.md §5)
 *
 * Four states, and the order matters:
 *
 *   loading  door shut, rope across it, nothing tappable. This is the spinner.
 *   shut     assets decoded. Tap to knock.
 *   open     the door has swung open onto blackness — but the ROPE IS STILL UP.
 *            You can see in; you are not in. This is where the bouncers walk
 *            out of the dark in Phase 1.
 *   entering the rope comes down and you go inside.
 *
 * First visit walks all four. A returning visit skips `shut` — the door is
 * already open when the page finishes loading, because they know you, and one
 * tap on the darkness takes you in. That asymmetry is the whole point: being
 * recognised should cost you one gesture fewer, every single time.
 *
 * No SSR means first paint waits on the bundle. Normally a real cost, free
 * here, because the first thing anyone sees is a closed door and a closed door
 * is an excellent loading screen.
 */

type DoorState = 'loading' | 'shut' | 'open' | 'entering';

const SWING_MS = 620;
const ROPE_MS = 420;
/** Beat between "loaded" and the door swinging on a return visit. A hard cut
 *  reads as a rendering glitch; this reads as someone opening the door. */
const RETURN_DELAY_MS = 260;

export function Door({ known, onOpen }: { known: boolean; onOpen: () => void }) {
  const [state, setState] = useState<DoorState>('loading');

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];

    // Two things race to declare the door ready — the decode promise and the
    // 4s fuse — and whichever loses must do nothing at all. Without this
    // latch, knocking inside the first four seconds opened the door and then
    // the late fuse shoved it shut again.
    let settled = false;
    const ready = () => {
      if (cancelled || settled) return;
      settled = true;

      // The door is usable, so nothing downstream is waiting on the network
      // any more. Warm the background tiers now, in idle time. Both are CSS
      // backgrounds that would otherwise not be fetched until the instant
      // they first need to be drawn — `arm_extender` when the forearm leaves
      // its resting place, and the pressed slab under the very first tap.
      //
      // Buttons first: they are on screen one tap from here, the arms several.
      warmButtonSprites();
      warmArmSprites();
      warmSingSprites();

      if (known) {
        // A guard is on their payroll. The door opens itself — no knock.
        timers.push(window.setTimeout(() => !cancelled && setState('open'), RETURN_DELAY_MS));
      } else {
        setState('shut');
      }
    };

    // Hard ceiling: if a sprite hangs, carry on without it after 4s.
    timers.push(window.setTimeout(ready, 4000));
    void preloadImages(DOOR_SPRITES).then(ready);

    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
    };
  }, [known]);

  const handleTap = useCallback(() => {
    setState((current) => {
      switch (current) {
        case 'shut':
          // Knock. The door swings onto black — the rope stays up.
          // Phase 1: this is the cue for Armane and Versace to step into frame.
          //
          // It is also the user gesture that unlocks the audio context on
          // mobile; Howler's unlock call belongs exactly here.
          return 'open';
        case 'open':
          // The rope comes down and the bouncers take over.
          window.setTimeout(onOpen, ROPE_MS);
          return 'entering';
        default:
          return current; // loading / entering — ignore
      }
    });
  }, [onOpen]);

  const isLoading = state === 'loading';
  const doorOpen = state === 'open' || state === 'entering';
  const ropeDown = state === 'entering';

  return (
    <div
      className="fixed inset-0 grid place-items-center bg-[radial-gradient(ellipse_at_50%_40%,#241a12_0%,#0a0705_70%)]"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <button
        type="button"
        onClick={handleTap}
        disabled={isLoading}
        aria-label={isLoading ? 'Učitavanje' : doorOpen ? 'Uđi' : 'Pokucaj'}
        // The frame is the hit target in every state — after the door swings,
        // the thing you tap is the darkness in the doorway.
        className="relative aspect-[3/5] w-[min(58vw,260px)] disabled:cursor-wait"
        style={{ perspective: '900px' }}
      >
        {/* The doorway: what is behind the door. Nothing, so far. In Phase 1
            the bouncers stand here, lit from inside the flat. */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-t-md"
          style={{
            background: 'radial-gradient(ellipse at 50% 62%, #120c08 0%, #000 78%)',
            boxShadow: 'inset 0 0 0 3px #3a2415, inset 0 14px 40px rgba(0,0,0,.95)',
          }}
        />

        {/* The door itself. Swings on its left hinge. */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-t-md transition-transform ease-out"
          style={{
            background: 'linear-gradient(175deg,#4a2f1d,#2a1a10)',
            boxShadow:
              'inset 0 0 0 3px #6b452b, inset 0 0 60px rgba(0,0,0,.7), 0 30px 80px rgba(0,0,0,.6)',
            transformOrigin: 'left center',
            transformStyle: 'preserve-3d',
            transform: doorOpen ? 'rotateY(-74deg) translateZ(1px)' : 'rotateY(0deg)',
            transitionDuration: `${SWING_MS}ms`,
          }}
        >
          {/* Handle, on the swinging edge. */}
          <span className="absolute right-4 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-bulb/70 shadow-[0_0_12px_rgba(255,217,138,.5)]" />
        </span>

        {/* The velvet rope — a sibling of the door, not a child, so it stays
            hooked across the frame while the door swings behind it. The rope,
            not the door, is what actually keeps you out. It drops last. */}
        <span
          aria-hidden
          className="absolute left-[-14%] right-[-14%] top-[58%] h-2 rounded-full bg-velvet transition-all ease-in"
          style={{
            transform: ropeDown ? 'translateY(130px) rotate(-9deg)' : 'none',
            opacity: ropeDown ? 0 : 1,
            boxShadow: '0 2px 10px rgba(0,0,0,.6)',
            transitionDuration: `${ROPE_MS}ms`,
          }}
        />
      </button>

      <p className="pointer-events-none absolute bottom-10 text-xs uppercase tracking-[0.3em] text-paper/30">
        {isLoading ? '...' : doorOpen ? 'uđi' : 'kucni'}
      </p>
    </div>
  );
}
